<template>
    <div>
        <ui-section title="Fase de habilitações e divergências" description="Situação do prazo e finalização da fase.">
            <div v-if="loading && !data" class="py-6"><v-progress-linear indeterminate color="primary" /></div>
            <div v-else-if="data" class="verification-phase">
                <div>
                    <ui-badge :text="phaseBadge.text" :tone="phaseBadge.tone" />
                    <p class="ui-small mt-2 mb-0">{{ phaseText }}</p>
                    <p v-if="data.finished_at" class="ui-small ui-muted mb-0">
                        Finalizada por {{ data.finished_by_name || "—" }} em {{ data.finished_at | defaultDate("DD/MM/YYYY HH:mm") }}.
                    </p>
                </div>
                <v-spacer></v-spacer>
                <v-btn v-if="!data.status.finished" color="primary" depressed :loading="acting" @click="finish">
                    <v-icon left small>mdi-flag-checkered</v-icon>
                    Finalizar habilitação/divergência
                </v-btn>
                <v-btn v-else outlined color="primary" :loading="acting" @click="reopen">
                    <v-icon left small>mdi-restore</v-icon>
                    Reabrir fase
                </v-btn>
            </div>
        </ui-section>

        <ui-section title="Prazos de habilitação e divergência" description="Até o prazo interno o site recebe pedidos dos credores." class="mt-4">
            <v-row v-if="form">
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.start_event_at" type="date" label="Início do prazo de hab/div" outlined dense hide-details="auto" clearable />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.end_event_at" type="date" label="Prazo interno de hab/div (sistema aberto até)" outlined dense hide-details="auto" clearable
                        hint="Até este dia o site recebe pedidos de habilitação e divergência." persistent-hint />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field v-model="form.habdiv_legal_deadline_at" type="date" label="Prazo real de hab/div (legal)" outlined dense hide-details="auto" clearable
                        hint="Prazo do art. 7º, § 1º. Libera a análise de ofício automática na Central." persistent-hint />
                </v-col>
            </v-row>
        </ui-section>

        <ui-section title="Responsáveis internos" description="Quem conduz a verificação de créditos deste caso." class="mt-4">
            <v-row v-if="form">
                <v-col cols="12" md="6">
                    <v-autocomplete v-model="form.verification_responsible_admin_id" :items="admins" item-text="name" item-value="id" label="Responsável"
                        outlined dense hide-details="auto" clearable no-data-text="Nenhum administrador encontrado" />
                </v-col>
                <v-col cols="12" md="6">
                    <v-autocomplete v-model="form.verification_coresponsible_admin_id" :items="coresponsibleOptions" item-text="name" item-value="id" label="Corresponsável"
                        outlined dense hide-details="auto" clearable no-data-text="Nenhum administrador encontrado" />
                </v-col>
            </v-row>
            <div class="d-flex justify-end mt-4">
                <v-btn color="primary" depressed :loading="saving" :disabled="!dirty" @click="save">
                    <v-icon left small>mdi-content-save-outline</v-icon>
                    Salvar cadastro
                </v-btn>
            </div>
        </ui-section>
    </div>
</template>

<script>
import axios from "axios";
import { formatCaseDate } from "@/common/util/credit-analysis-case.util";

const FIELDS = ["start_event_at", "end_event_at", "habdiv_legal_deadline_at", "verification_responsible_admin_id", "verification_coresponsible_admin_id"];

// Central de Creditos, aba "Cadastro da verificacao": prazos de hab/div,
// responsavel e corresponsavel internos e a finalizacao da fase.
export default {
    name: "CreditAnalysisVerificationForm",

    props: {
        clientId: { type: [Number, String], required: true },
        admins: { type: Array, default: () => [] },
    },

    data: () => ({ data: null, form: null, loading: false, saving: false, acting: false }),

    computed: {
        coresponsibleOptions() {
            return this.admins.filter((admin) => Number(admin.id) !== Number(this.form && this.form.verification_responsible_admin_id));
        },

        dirty() {
            if (!this.data || !this.form) return false;
            return FIELDS.some((field) => (this.form[field] || null) !== (this.data[field] || null));
        },

        phaseBadge() {
            const status = this.data.status;
            if (status.finished) return { text: "Fase finalizada", tone: "neutral" };
            if (status.is_open) return { text: "Prazo aberto", tone: "success" };
            if (status.is_before) return { text: "Prazo não iniciado", tone: "info" };
            if (status.is_after) return { text: "Prazo encerrado", tone: "neutral" };
            return { text: "Sem prazo cadastrado", tone: "warning" };
        },

        phaseText() {
            const status = this.data.status;
            if (status.finished) return "O site não recebe mais pedidos de habilitação e divergência neste caso, mesmo dentro do prazo cadastrado.";
            if (status.is_open) return `O site recebe pedidos até ${formatCaseDate(this.data.end_event_at)}. Ao finalizar, o envio é encerrado na hora.`;
            if (status.is_before) return `O site passa a receber pedidos em ${formatCaseDate(this.data.start_event_at)}.`;
            if (status.is_after) return `O prazo interno terminou em ${formatCaseDate(this.data.end_event_at)}. Finalize a fase para registrar o encerramento.`;
            return "Cadastre o início e o prazo interno para o site receber pedidos.";
        },
    },

    watch: {
        clientId: { immediate: true, handler() { this.load(); } },
    },

    methods: {
        fill(content) {
            this.data = content;
            this.form = FIELDS.reduce((form, field) => ({ ...form, [field]: content[field] || null }), {});
        },

        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
        },

        async load() {
            if (!this.clientId) return;
            this.loading = true;
            try {
                const response = await axios.get(`/api/admin/credit-analysis/cases/${this.clientId}/verification`);
                this.fill(response.data.content);
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        async save() {
            this.saving = true;
            try {
                const payload = FIELDS.reduce((body, field) => ({ ...body, [field]: this.form[field] || null }), {});
                const response = await axios.put(`/api/admin/credit-analysis/cases/${this.clientId}/verification`, payload);
                this.fill(response.data.content);
                this.$dialog.notify.success("Cadastro da verificação salvo.", { position: "top-right", timeout: 3000 });
                this.$emit("changed");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.saving = false;
            }
        },

        async finish() {
            const proceed = await this.$dialog.confirm({
                title: "Finalizar habilitação/divergência",
                text: "O site deixa de receber pedidos de habilitação e divergência neste caso a partir de agora, mesmo dentro do prazo cadastrado. A fase pode ser reaberta depois. Continuar?",
                actions: { false: "Cancelar", true: "Finalizar" },
            });
            if (proceed) this.act("finish", "Fase de habilitação/divergência finalizada.");
        },

        async reopen() {
            const proceed = await this.$dialog.confirm({
                title: "Reabrir fase",
                text: "A fase volta a seguir o prazo cadastrado: se ele estiver aberto, o site volta a receber pedidos. Continuar?",
                actions: { false: "Cancelar", true: "Reabrir" },
            });
            if (proceed) this.act("reopen", "Fase reaberta.");
        },

        async act(action, message) {
            this.acting = true;
            try {
                const response = await axios.post(`/api/admin/credit-analysis/cases/${this.clientId}/verification/${action}`);
                this.fill(response.data.content);
                this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
                this.$emit("changed");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.acting = false;
            }
        },
    },
};
</script>

<style scoped>
.verification-phase {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
}
</style>
